import { useCallback, useEffect, useState } from 'react';
import PaymentBlock from '../payments/PaymentBlock';
import { payReservation } from '../../openapi/reservations/reservations';
import { Show } from '../../openapi/model';
import Checkable from '../components/Checkable';
import RangeSlider from '../components/RangeSlider';
import { formatCurrency } from '../helpers/formatters';
import { II18nLanguageObj, useLang } from '../hooks/useLanguage';
import { useMarketing } from '../hooks/useMarketing.tsx';
import { calculateProcessingFee } from '../helpers/calculateProcessingFee';
import { buildGa4ItemsFromCart } from '../helpers/ga4Items';

interface PaymentMethod {
    slug: string;
    name: string;
    nameI18n: II18nLanguageObj;
    methodType: string;
    agent_only: boolean;
    methodInfo: object | null;
    percentage: number;
    usd: number;
}

// Minimal shape the marketing `purchase` event needs from a cart line.
// Satisfied by both useCart's ICartItem and the pendingCart built in
// ReservationDetails. `price` is in cents (converted to major units before
// it reaches the tracking payload).
type TrackableCartItem = {
    ticketTypeId: number;
    ticketTypeName?: string;
    ticketOptionId: number;
    ticketOptionName?: string;
    price: number;
    quantity: number;
};

function ReservationPendingOrder({
    order,
    allOrders,
    paymentMethods,
    reservationId,
    reservationHash,
    reloadOrdersAndReservation,
    show,
    onPaymentMethodChange,
    onAmountChange,
    cart,
}: {
    order: any;
    allOrders: any[];
    paymentMethods: PaymentMethod[];
    reservationId: number;
    reservationHash: string;
    reloadOrdersAndReservation: () => Promise<void>;
    show: Show;
    onPaymentMethodChange?: (paymentMethod: PaymentMethod | null) => void;
    onAmountChange?: (amount: number) => void;
    cart?: TrackableCartItem[];
}) {
    const { getI18n, t } = useLang();
    const { addPaymentInfo } = useMarketing();
    // When the show absorbs the card fee, the guest pays no surcharge.
    const absorbProcessingFee = !!show?.marketing?.absorbProcessingFee;
    const maxAmount = Math.max(
        0,
        order.amount -
            order?.transactions.reduce((acc, trx) => acc + (trx.finished ? trx.amount : 0), 0),
    );
    const [amount, setAmount] = useState(maxAmount);

    useEffect(() => {
        onAmountChange?.(amount);
    }, [amount, onAmountChange]);
    const [activePercent, setActivePercent] = useState<number | null>(null);

    // Store the SLUG, not the object. The backend regenerates a fresh
    // Stripe SetupIntent every time the payment methods are fetched
    // (StripeService.getPaymentMethodInfo), so the methodInfo.clientSecret
    // on the first-load object goes stale the moment paymentMethods
    // refetches. Deriving the current method from the latest paymentMethods
    // array on every render guarantees we always hand the freshest
    // clientSecret to Stripe Elements.
    const [pickedPaymentMethodSlug, setPickedPaymentMethodSlug] = useState<string | null>(() => {
        return paymentMethods.find((pm) => pm.agent_only === false)?.slug || null;
    });
    const pickedPaymentMethod = pickedPaymentMethodSlug
        ? paymentMethods.find((pm) => pm.slug === pickedPaymentMethodSlug) || null
        : null;

    useEffect(() => {
        onPaymentMethodChange?.(pickedPaymentMethod);
    }, [pickedPaymentMethod, onPaymentMethodChange]);

    // Auto-reselect when slug is null (user just completed a transaction and
    // we cleared it to force Stripe Elements to unmount) or when the current
    // slug is no longer available in paymentMethods.
    useEffect(() => {
        const validSlug =
            pickedPaymentMethodSlug &&
            paymentMethods.some((pm) => pm.slug === pickedPaymentMethodSlug);
        if (!validSlug) {
            const first = paymentMethods.find((pm) => pm.agent_only === false);
            setPickedPaymentMethodSlug(first?.slug || null);
        }
    }, [paymentMethods, pickedPaymentMethodSlug]);

    const [splitOpen, setSplitOpen] = useState(false);

    const hasMorePaymentMethods = paymentMethods.filter((e) => e.agent_only === false).length > 1;

    // Calculate processing fee based on amount and selected payment method.
    // Absorbing shows charge the guest no surcharge for STRIPE card payments
    // only (mirrors the backend, which absorbs solely for method_type 'stripe');
    // other method types keep their configured fee even on absorbing shows.
    const absorbsStripeFee = absorbProcessingFee && pickedPaymentMethod?.methodType === 'stripe';
    // Net rate charges the guest exactly the net price with no surcharge, mirroring
    // the backend, which zeroes the processing fee for net_rate payments.
    const isNetRate = pickedPaymentMethod?.methodType === 'net_rate';
    const processingFee =
        pickedPaymentMethod && !absorbsStripeFee && !isNetRate
            ? calculateProcessingFee(amount, {
                  percentage: Number(pickedPaymentMethod.percentage) || 0,
                  usd: Number(pickedPaymentMethod.usd) || 0,
              })
            : 0;

    const handlePercentClick = (percent: number) => {
        const calculated = Math.round((maxAmount * percent) / 100);
        setAmount(calculated);
        setActivePercent(percent);
    };

    // on order change, recalculate the amount
    useEffect(() => {
        setAmount(maxAmount);
    }, [maxAmount, setAmount]);

    useEffect(() => {
        const ratio = amount / maxAmount;
        const percents = [25, 50, 75];
        const matched = percents.find((p) => Math.abs(ratio - p / 100) < 0.01); // 1% tolerance
        setActivePercent(matched ?? null);
    }, [amount, maxAmount]);

    const splitLimit = show?.venueAllowSplitPayment ?? 1;
    // Net rate is a single charge of the full net order - never offer split.
    const showSplitPayment =
        !isNetRate &&
        (splitLimit === 0 || splitLimit > 1) &&
        (splitLimit === 0 || splitLimit > order.transactions.length + 1);

    const startTransaction = useCallback(
        async (paymentInfo: Record<string, any>) => {
            if (!pickedPaymentMethod) {
                console.error('No payment method selected');
                return;
            }
            // TODO: start the transaction on the other end and refresh everything once finished
            try {
                const cartItems = buildGa4ItemsFromCart(cart ?? [], show?.title);
                const paymentType =
                    amount < maxAmount
                        ? 'split_payment'
                        : pickedPaymentMethod.methodType === 'stripe'
                          ? 'credit_card'
                          : pickedPaymentMethod.methodType || pickedPaymentMethod.slug;

                if (cartItems.length > 0) {
                    addPaymentInfo({
                        items: cartItems,
                        value: (amount + processingFee) / 100,
                        currency: 'USD',
                        payment_type: paymentType,
                    });
                }

                const result = await payReservation(
                    reservationId,
                    {
                        // auto pull data (on null)
                        orderId: null,
                        amount: maxAmount !== amount ? amount : null,
                        // method
                        paymentMethodSlug: pickedPaymentMethod?.slug,
                        processingFee: processingFee,
                        transactionInfo: paymentInfo,
                    },
                    {
                        reservationHash,
                    },
                );

                // Refetch BEFORE clearing the picked slug. The auto-reselect
                // effect fires on slug change and would otherwise re-pick from
                // the stale paymentMethods array whose methodInfo.clientSecret
                // points to the SetupIntent we just confirmed — Stripe then
                // 400s /v1/elements/sessions on the next Elements mount. By
                // awaiting the refetch first we guarantee the auto-reselect
                // lands on a fresh, non-terminal SetupIntent.
                setSplitOpen(false);
                await reloadOrdersAndReservation();
                setPickedPaymentMethodSlug(null);
            } catch (error) {
                console.error('Error starting transaction:', error);
                alert(t('errors.transactionFailed'));
            }
        },
        [
            pickedPaymentMethod,
            processingFee,
            reservationId,
            reservationHash,
            reloadOrdersAndReservation,
            t,
            maxAmount,
            amount,
            allOrders,
            cart,
            show?.title,
            addPaymentInfo,
        ],
    );

    return (
        <div className="fireknife-reservation-pending-order">
            <div className="fireknife-reservation-pending-order__amount-form-title-box">
                <div className="fireknife-reservation-pending-order__amount-form-title">
                    {t('widgetReservationInfo.paymentDetails.payableAmount')}
                </div>
                <div className="fireknife-reservation-pending-order__amount-form-desc">
                    {formatCurrency((amount + processingFee) / 100, t('widgetLocale'))}
                </div>
                {showSplitPayment && (
                    <button
                        type="button"
                        className="fireknife-reservation-pending-order__split-link"
                        onClick={() => {
                            const next = !splitOpen;
                            setSplitOpen(next);
                            if (!next) {
                                setAmount(maxAmount);
                            }
                        }}
                    >
                        {t('widgetReservationInfo.paymentDetails.wantToSplit')}
                    </button>
                )}
                {pickedPaymentMethod && !absorbsStripeFee && !isNetRate && (
                    <div className="fireknife-reservation-pending-order__processing-fee">
                        <small>
                            {t('widgetReservationInfo.paymentDetails.processingFeeDisclaimer')}
                        </small>
                    </div>
                )}
            </div>

            <div className="fireknife-payment-methods">
                {hasMorePaymentMethods && paymentMethods ? (
                    <h2 className="fireknife-payment-methods__title">
                        {t('widgetReservationInfo.paymentMethods.title')}
                    </h2>
                ) : null}
                {hasMorePaymentMethods && paymentMethods && (
                    <div className="fireknife-payment-methods__checkables">
                        {paymentMethods
                            .filter((e) => e.agent_only === false)
                            .map((paymentMethod, paymentMethodIdx) => (
                                <Checkable
                                    key={`${paymentMethodIdx}-${paymentMethod.slug}`}
                                    label={getI18n(paymentMethod.name, paymentMethod.nameI18n)}
                                    attributes={{
                                        name: 'payment-method',
                                        value: paymentMethod.slug,
                                        checked: pickedPaymentMethod?.slug === paymentMethod.slug,
                                        onChange: () =>
                                            setPickedPaymentMethodSlug(paymentMethod.slug),
                                    }}
                                />
                            ))}
                    </div>
                )}
            </div>

            {splitOpen && (
                <div className="fireknife-reservation-pending-order__split-section">
                    <label className="fireknife-reservation-pending-order__amount-form-label fireknife-form-label">
                        {t('widgetReservationInfo.paymentMethods.amountLabel')}
                    </label>
                    <div className="fireknife-reservation-pending-order__form-group">
                        <div className="fireknife-reservation-pending-order__form-box">
                            <div className="fireknife-form-control-box">
                                <div className="fireknife-reservation-pending-order__amount-prefix">
                                    $
                                </div>
                                <input
                                    className="fireknife-reservation-pending-order__amount fireknife-form-control"
                                    type="text"
                                    inputMode="numeric"
                                    value={amount / 100}
                                    onChange={(e) => {
                                        const parsed = parseFloat(e.target.value);
                                        if (isNaN(parsed)) {
                                            return;
                                        } else {
                                            setAmount(
                                                Math.max(0, Math.min(maxAmount, parsed * 100)),
                                            );
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="fireknife-reservation-pending-order__form-box">
                            <div className="fireknife-reservation-pending-order__precent-btns">
                                {[25, 50, 75].map((percent) => (
                                    <button
                                        key={percent}
                                        className={`fireknife-reservation-pending-order__precent-btn fireknife-btn${
                                            activePercent === percent
                                                ? ' fireknife-reservation-pending-order__precent-btn--active'
                                                : ''
                                        }`}
                                        type="button"
                                        onClick={() => handlePercentClick(percent)}
                                    >
                                        {percent}%
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="fireknife-reservation-pending-order__form-box fireknife-reservation-pending-order__form-box--range">
                            <div className="fireknife-form-control-box">
                                <RangeSlider>
                                    <input
                                        className="fireknife-reservation-pending-order__range fireknife-form-control"
                                        type="range"
                                        min="1"
                                        max={parseFloat((maxAmount / 100).toFixed(2))}
                                        value={amount / 100}
                                        onChange={(e) =>
                                            setAmount(
                                                Math.max(
                                                    0,
                                                    Math.min(
                                                        maxAmount,
                                                        parseFloat(e.target.value) * 100,
                                                    ),
                                                ),
                                            )
                                        }
                                    />
                                </RangeSlider>
                            </div>
                        </div>
                    </div>
                    {amount !== maxAmount && (
                        <div className="fireknife-reservation-pending-order__split-notice">
                            <small>
                                {t('widgetReservationInfo.paymentDetails.remainingNotice', {
                                    amount: formatCurrency(
                                        parseFloat(((maxAmount - amount) / 100).toFixed(2)),
                                        t('widgetLocale'),
                                    ),
                                })}
                            </small>
                        </div>
                    )}
                    {pickedPaymentMethod && !absorbsStripeFee && !isNetRate && (
                        <div className="fireknife-reservation-pending-order__split-payment-fee-notice">
                            <small>
                                {t('widgetReservationInfo.paymentDetails.processingFeeDisclaimer')}
                            </small>
                        </div>
                    )}
                </div>
            )}
            {pickedPaymentMethod && (
                <PaymentBlock
                    order={order}
                    allOrders={allOrders}
                    paymentMethod={pickedPaymentMethod}
                    onStartTransaction={startTransaction}
                    onCancel={hasMorePaymentMethods ? () => setPickedPaymentMethodSlug(null) : null}
                    amount={amount}
                    maxAmount={maxAmount}
                />
            )}
        </div>
    );
}
export default ReservationPendingOrder;
